// Spuren offline rechnen: Commit (auf neue Audiospur), Freeze (Einfrieren) und Stems.
// Nutzt denselben Export wie der Mixdown (engine.bounce), auf einer Kopie des Projekts mit nur den gewünschten Spuren.
import type { Project, Track } from '@srs/shared';
import { engine } from './engine.ts';
import { clipEnd } from '../edit/ops.ts';
import { useStudio } from '../state/store.ts';
import { uid } from '../state/demo.ts';
import { api } from '../api/client.ts';
import { encodeWav } from './recorder.ts';
import { freezeSig } from '../edit/mixops.ts';
import { ensureWarp } from '../edit/warp.ts';

/** Ende des Songs: letzter Clip */
export const songEnd = (p: Project) => Math.max(1, ...p.clips.map(clipEnd));

/**
 * Projekt nur mit diesen Spuren.
 *  stem:   wie im Mix (Fader, Pan, Sends, Busse), andere Spuren raus
 *  freeze: Spur vor dem Fader, nach dem Panner (Inserts, Instrument, Pan), ohne Sends und Master-Plugins
 *  commit: wie freeze, aber Panorama in der Mitte (die neue Spur bekommt Pan und Fader der alten)
 */
export function isolate(p: Project, ids: string[], how: 'stem' | 'freeze' | 'commit', opts: { inserts?: boolean; effects?: boolean; master?: boolean } = {}): Project {
  const q = structuredClone(p);
  q.tracks = q.tracks.filter((t) => ids.includes(t.id)).map((t): Track => {
    const x: Track = { ...t, mute: false, solo: false, frozen: undefined };
    if (how !== 'stem') {
      x.volumeDb = 0; x.sends = []; x.output = undefined;
      const lanes = Object.fromEntries(Object.entries(t.automation?.lanes ?? {}).filter(([k]) => k.startsWith('ins:') || (how === 'freeze' && k === 'pan')));
      x.automation = t.automation ? { ...t.automation, lanes } : undefined;
      if (how === 'commit') x.pan = 0;
      if (opts.inserts === false) x.inserts = [];
    } else if (!opts.effects) { x.sends = []; if (x.automation?.lanes) x.automation = { ...x.automation, lanes: Object.fromEntries(Object.entries(x.automation.lanes).filter(([k]) => !k.startsWith('send:'))) }; }
    return x;
  });
  q.clips = q.clips.filter((c) => ids.includes(c.trackId));
  if (how !== 'stem') { q.busses = []; }
  if (how !== 'stem' || !opts.master) { q.masterInserts = []; q.masterAutomation = undefined; q.masterDb = 0; q.mixBus = undefined; }
  return q;
}

/** Gleiche Kanäle? (Mono-Quelle durch den Panner in der Mitte) → Mono mit +3 dB zurück, damit Pan-Gesetz und Pegel stimmen */
function monoIfCentered(b: AudioBuffer): AudioBuffer {
  if (b.numberOfChannels < 2) return b;
  const l = b.getChannelData(0), r = b.getChannelData(1);
  let diff = 0, sum = 0;
  for (let i = 0; i < l.length; i += 7) { diff += Math.abs(l[i] - r[i]); sum += Math.abs(l[i]); }
  if (sum < 1e-9 || diff / sum > 1e-4) return b;
  const m = new AudioBuffer({ length: b.length, numberOfChannels: 1, sampleRate: b.sampleRate }), d = m.getChannelData(0);
  for (let i = 0; i < l.length; i++) d[i] = l[i] * Math.SQRT2;
  return m;
}
/** Über 0 dBFS? Dann leiser speichern (sonst schneidet die 24-Bit-Datei ab) und den Unterschied als Gain zurückgeben */
export function headroom(b: AudioBuffer): number {
  let pk = 0;
  for (let c = 0; c < b.numberOfChannels; c++) { const d = b.getChannelData(c); for (let i = 0; i < d.length; i++) { const v = d[i] < 0 ? -d[i] : d[i]; if (v > pk) pk = v; } }
  if (pk <= 0.999) return 0;
  const k = 0.999 / pk;
  for (let c = 0; c < b.numberOfChannels; c++) { const d = b.getChannelData(c); for (let i = 0; i < d.length; i++) d[i] *= k; }
  return Math.round(-20 * Math.log10(k) * 100) / 100;
}
/** Ausschnitt [a, b) eines Puffers */
export function slice(b: AudioBuffer, a: number, e: number): AudioBuffer {
  const sr = b.sampleRate, i0 = Math.max(0, Math.round(a * sr)), n = Math.max(1, Math.min(b.length, Math.round(e * sr)) - i0);
  const out = new AudioBuffer({ length: n, numberOfChannels: b.numberOfChannels, sampleRate: sr });
  for (let c = 0; c < b.numberOfChannels; c++) out.copyToChannel(b.getChannelData(c).subarray(i0, i0 + n), c);
  return out;
}

/** Spur rechnen; Ergebnis beginnt bei `from` */
export async function renderTrack(p: Project, trackId: string, how: 'freeze' | 'commit', opts: { from?: number; to?: number; tail?: number; inserts?: boolean } = {}): Promise<AudioBuffer> {
  const from = opts.from ?? 0, to = (opts.to ?? songEnd(p)) + (opts.tail ?? 2);
  const q = isolate(p, [trackId], how, { inserts: opts.inserts });
  const full = await engine.bounce(q, to);
  const cut = from > 0 ? slice(full, from, to) : full;
  return how === 'commit' ? monoIfCentered(cut) : cut;
}

// ---- Freeze und Commit als Befehle ----

/** Neue Audiodatei hochladen und die vorläufige Id ersetzen, sobald der Server sie kennt */
function uploadLater(id: string, buf: AudioBuffer) {
  const st = useStudio.getState();
  if (!st.token) return;
  void api.uploadWav(encodeWav(buf), `${id}.wav`, st.project.id).then((m) => { if (m) { engine.buffers.set(m.id, buf); useStudio.getState().replaceSourceId(id, m.id); } }).catch(() => undefined);
}

/** Spuren einfrieren: spielen danach ihre gerechnete Fassung (Inserts und Instrument ruhen) */
export async function freezeTracks(ids: string[]): Promise<number> {
  let n = 0;
  for (const id of ids) {
    const p = useStudio.getState().project, t = p.tracks.find((x) => x.id === id);
    if (!t || t.kind === 'bus' || !p.clips.some((c) => c.trackId === id)) continue;
    const buf = await renderTrack(p, id, 'freeze'), gainDb = headroom(buf);
    const sid = uid('src'); engine.buffers.set(sid, buf);
    useStudio.getState().commit(`Einfrieren: ${t.name}`, (q) => { const x = q.tracks.find((y) => y.id === id); if (x) x.frozen = { sourceId: sid, duration: buf.duration, sig: freezeSig(q, x), ...(gainDb ? { gainDb } : {}) }; });
    uploadLater(sid, buf); n++;
  }
  return n;
}
export function unfreezeTracks(ids: string[]) {
  useStudio.getState().commit(ids.length === 1 ? 'Auftauen' : 'Spuren auftauen', (q) => { for (const t of q.tracks) if (ids.includes(t.id)) t.frozen = undefined; });
}

/**
 * Commit: Spur (Inserts, Instrument, Elastic, Plugin-Automation) auf eine neue Audiospur rechnen.
 * Die neue Spur übernimmt Fader, Pan, Sends, Ausgang und deren Automation; Original: ausblenden und stumm, behalten oder löschen.
 */
export async function commitTracks(ids: string[], o: { from?: number; to?: number; inserts: boolean; original: 'hide' | 'keep' | 'delete' }): Promise<string[]> {
  const made: string[] = [];
  for (const id of ids) {
    const p = useStudio.getState().project, t = p.tracks.find((x) => x.id === id);
    if (!t || t.kind === 'bus' || !p.clips.some((c) => c.trackId === id)) continue;
    const from = o.from ?? 0;
    const buf = await renderTrack(p, id, 'commit', { from, to: o.to, inserts: o.inserts }), gainDb = headroom(buf);
    const sid = uid('src'); engine.buffers.set(sid, buf);
    const nid = uid('t');
    useStudio.getState().commit(`Commit: ${t.name}`, (q) => {
      const src = q.tracks.find((y) => y.id === id)!, i = q.tracks.indexOf(src);
      const keepLanes = Object.fromEntries(Object.entries(src.automation?.lanes ?? {}).filter(([k]) => !k.startsWith('ins:') || !o.inserts));
      const nt: Track = { id: nid, name: `${src.name} (Commit)`, color: src.color, kind: 'audio', input: { kind: 'browser' }, armed: false, mute: false, solo: false,
        volumeDb: src.volumeDb, pan: src.pan, sends: structuredClone(src.sends), output: src.output, folderId: src.folderId,
        inserts: o.inserts ? [] : structuredClone(src.inserts), automation: src.automation ? { ...structuredClone(src.automation), lanes: keepLanes } : undefined, elastic: src.elastic };
      q.tracks.splice(i + 1, 0, nt);
      const c = { id: uid('c'), trackId: nid, kind: 'audio' as const, name: `${src.name} (Commit)`, start: from, offset: 0, duration: buf.duration, sourceId: sid, fadeIn: 0, fadeOut: 0, ...(gainDb ? { gainDb } : {}), by: { userId: useStudio.getState().user.id, at: new Date().toISOString() } };
      q.clips.push(c);
      if (nt.elastic) ensureWarp(q, c);
      if (o.original === 'hide') { src.hidden = true; src.mute = true; }
      if (o.original === 'delete') { q.tracks = q.tracks.filter((y) => y.id !== id); q.clips = q.clips.filter((y) => y.trackId !== id); q.takes = q.takes.filter((y) => y.trackId !== id); }
    });
    uploadLater(sid, buf); made.push(nid);
  }
  return made;
}
